import React, { memo, useEffect, useRef } from 'react';
import { View, StyleSheet, Animated, TouchableOpacity } from 'react-native';
import { TOGGLE_KEYS } from '../../constants/ExpenseCategories';
import BaseText from '../BaseText';
import { Color, CommonProperties, CommonStyles } from '../../styles';
import { useDispatch, useSelector } from 'react-redux';
import { setExpenseValue } from '../../store/actions/expense';
import { scale, scaleVertically } from '../../utils/scale.utils';
import { selectExpenseStates } from '../../store/selectors/expense.selector';

const ExpenseToggle = memo(({ toggle, value, isEditable, onValueChange }) => {
  const animation = useRef(new Animated.Value(value ? 1 : 0)).current;

  const handleToggle = () => {
    if (isEditable) {
      onValueChange(!value);
      Animated.timing(animation, {
        toValue: value ? 0 : 1,
        duration: 300,
        useNativeDriver: false,
      }).start();
    }
  };

  const circlePosition = animation.interpolate({
    inputRange: [0, 1],
    outputRange: ['4%', '52%'],
  });

  const backgroundColor = animation.interpolate({
    inputRange: [0, 1],
    outputRange: [Color.grayText2, Color.active],
  });

  useEffect(() => {
    Animated.timing(animation, {
      toValue: value ? 1 : 0,
      duration: 300,
      useNativeDriver: false,
    }).start();
  }, [animation, value]);

  return (
    <View style={styles.wrapper}>
      <TouchableOpacity
        onPress={handleToggle}
        style={styles.toggleContainer}
        disabled={!isEditable}
      >
        <Animated.View style={[styles.activeBackground, { backgroundColor }]} />
        <Animated.View style={[styles.circle, { left: circlePosition }]} />
      </TouchableOpacity>
      <BaseText style={{ ...CommonProperties.custom, ...CommonProperties.px1 }}>
        {toggle.label}
      </BaseText>
    </View>
  );
});

const ExpenseToggles = ({ actionType, subTab, isEditable }) => {
  const dispatch = useDispatch();
  const expenseStates = useSelector(selectExpenseStates)[actionType][subTab];
  return (
    <View style={styles.container}>
      {TOGGLE_KEYS.map((toggle) => (
        <ExpenseToggle
          key={toggle.key}
          toggle={toggle}
          value={expenseStates[toggle.key]}
          isEditable={isEditable}
          onValueChange={(value) => {
            dispatch(setExpenseValue({ actionType, subTab, key: toggle.key, value: value }));
          }}
        />
      ))}
    </View>
  );
};

const toggleSize = scale(25);
const circleSize = toggleSize - scale(3);
const base = scale(12 * 0.8);

export const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-around',
    alignItems: 'center',
    paddingTop: base,
    paddingBottom: base * 2 - scale(2),
    ...CommonProperties.px2,
    marginBottom: scaleVertically(4),
  },
  wrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    ...CommonStyles.py2,
  },
  toggleContainer: {
    width: toggleSize * 2,
    height: toggleSize,
    borderRadius: toggleSize / 2,
    backgroundColor: Color.grayText2,
    position: 'relative',
    justifyContent: 'center',
    marginRight: scale(8),
  },
  activeBackground: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    borderRadius: toggleSize / 2,
    zIndex: -1,
  },
  circle: {
    width: circleSize,
    height: circleSize,
    borderRadius: toggleSize / 2,
    backgroundColor: Color.accent,
    position: 'absolute',
  },
});

export default ExpenseToggles;
